import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AskIrisButton, secondaryButtonSx } from '@components'
import ScreenAbout from '@components/AskIris/ScreenAbout'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import useToast from '@hooks/useToast'
import { dsColorNew } from '@palette'
import PageWrapper from 'Pages/@PageWrapper'
import { Surface, SurfaceHeader } from 'Pages/DsRepricing/Setup/@components/Surface'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { useOfficeScope } from 'Pages/DsRepricing/Setup/@services/useOfficeScope'
import { toIso } from 'Pages/DsRepricing/@services/dateRange'
import { clearAskContext } from '@utils/askIris/send'

import Band from './@components/Band'
import Headline from './@components/Headline'
import ChangeGrid from './@components/ChangeGrid'
import ChangeLegend from './@components/ChangeLegend'
import PushesTable from './@components/PushesTable'
import MovementTable from './@components/MovementTable'
import MovementCard from './@components/MovementCard'
import CellDetailDialog from './@components/CellDetailDialog'
import AcrissDetailDialog from './@components/AcrissDetailDialog'
import { ErrorNote, InProgressNote, ModeNote, NoPassHint, NoPushesNote, NoTierNote } from './@components/States'
import { STORAGE_OFFICE, defaultView, readView, sampleKey, writeView } from './@services/viewState'
import { METHODOLOGY, METHODOLOGY_TITLE } from './@services/methodology'
import { gridEvidence, shouldClearAskContext, tableEvidence } from './@services/askEvidence'
import { excelBody } from './@services/excelRows'
import { downloadExcel, useLastChanges, usePushes } from './@services/useLastChanges'
import { noPushes } from './@services/windowLabel'
import { METRICS } from './@services/movementBars'


const readStored = () => { try { return localStorage.getItem(STORAGE_OFFICE) } catch { return null } }
const store = code => { try { if (code) localStorage.setItem(STORAGE_OFFICE, code) } catch { /* private mode */ } }

function Methodology() {
	return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
		{METHODOLOGY.map(m => <Box key={m.term}><b>{m.term}.</b> {m.text}</Box>)}
	</Box>
}

/**
 * DS Repricing › Last changes (CU-869fn0gcp): what the engine's pushes did to the account's prices vs its tier, per
 * ACRISS × pick-up date, between two instants. Opens already computed (the last office used, 7 days, the next 14
 * days, last 7 days, Total); every change of the band recomputes; the view lives in the URL (write-back). The server
 * computes everything (`/last-changes`); this screen renders. States: loading, error, no pass, pass in progress (the
 * newest pass has no `finished_at` yet: what shows is partial), no tier, simulation. Simulated decisions count like
 * written ones, marked SIM; the band's "Simulated" filter hides them (v1.5). Ask Iris: the header's "?" (help
 * mode), the three cards (icon), the map, the "Movement by ACRISS" table and the two dialogs (label).
 */
export default function DsRepricingLastChangesPage() {
	const loc = useLocation()
	const nav = useNavigate()
	const { showToast } = useToast()
	const offices = useOfficeScope()
	const todayIso = toIso(new Date())

	const [view, setView] = useState(() => readView(loc.search, todayIso, { lastOffice: readStored() }))
	// `sel` read once from the URL (the dialog opens on load); from then on the state leads and the URL follows
	const [cellOpen, setCellOpen] = useState(() => !!view.sel?.sipp)
	const [acrissOpen, setAcrissOpen] = useState(null)
	const [excelBusy, setExcelBusy] = useState(false)
	const [metric, setMetric] = useState(METRICS.PCT)     // the bars of "Movement by ACRISS": % or €
	const lastKey = useRef(null)

	// no office in the link nor remembered: the first of the catalogue, once it arrives
	useEffect(() => {
		if (!view.office && offices.offices.length) setView(v => ({ ...v, office: offices.offices[0].code }))
	}, [offices.offices, view.office])

	// write-back (replace), and the office remembered for the next visit
	useEffect(() => {
		const next = writeView(view)
		if (next !== loc.search) nav({ search: next }, { replace: true })
		store(view.office)
	}, [view])  // eslint-disable-line react-hooks/exhaustive-deps

	const { data, loading, refetching, error, reload } = useLastChanges(view)
	const pushes = usePushes(view, view.sel)

	// the evidence Iris holds goes when the sample or the window changes (Activity's rule)
	useEffect(() => {
		const key = sampleKey(view)
		if (shouldClearAskContext(lastKey.current, key)) clearAskContext(window.DSB)
		lastKey.current = key
	}, [view.office, view.lor, view.pickup_from, view.pickup_to, view.window, view.days, view.simulated])  // eslint-disable-line react-hooks/exhaustive-deps

	const change = useCallback(patch => setView(v => ({ ...v, ...patch, sel: 'sel' in patch ? patch.sel : (['office', 'lor', 'pickup_from', 'pickup_to', 'window', 'days'].some(k => k in patch) ? null : v.sel) })), [])
	const reset = () => { setView(defaultView(todayIso, view.office)); setCellOpen(false) }
	const selectPickup = d => { change({ sel: view.sel?.pickup === d && !view.sel?.sipp ? null : { pickup: d, sipp: null } }); setCellOpen(false) }
	const openCell = (sipp, d) => { change({ sel: { pickup: d, sipp } }); setAcrissOpen(null); setCellOpen(true) }
	const closeCell = () => setCellOpen(false)

	const cells = data?.cells || []
	const cell = useMemo(() => (view.sel?.sipp ? cells.find(c => c.sipp === view.sel.sipp && c.pickup_date === view.sel.pickup) || { sipp: view.sel.sipp, pickup_date: view.sel.pickup, status: 'no_decision', n_passes: 0 } : null), [cells, view.sel])
	const askable = !!data && !loading && !refetching
	const empty = !!data && !data.passes
	const inProgress = !!data?.window?.pass?.in_progress

	const excel = async () => {
		if (!data) return
		setExcelBusy(true)
		try {
			const name = await downloadExcel(excelBody(data, view, pushes.items))
			showToast.success(`Excel downloaded · ${name}`)
		} catch (e) {
			showToast.error(e.message || 'Could not prepare the Excel.')
		} finally {
			setExcelBusy(false)
		}
	}

	return <PageWrapper title={DS_REPRICING_ROUTES.LAST_CHANGES_TITLE} subtitle={DS_REPRICING_ROUTES.LAST_CHANGES_SUBTITLE}
		actions={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<ScreenAbout title={METHODOLOGY_TITLE} tooltip="How this is calculated"><Methodology/></ScreenAbout>
			<Button variant="outlined" onClick={excel} disabled={excelBusy || !data || empty} title="Two sheets: Rates (every cell) and Pushes (the selection below the map)"
				startIcon={<IconSeeker icon="file-excel" sx={{ fontSize: 13 }}/>} sx={{ ...secondaryButtonSx, height: 36, whiteSpace: 'nowrap' }}>Download Excel</Button>
		</Box>}>
		<Band view={view} offices={offices.offices} officesLoading={offices.loading} data={data} onChange={change} onReset={reset}/>

		{!!error && <ErrorNote error={error} onRetry={reload}/>}
		{!!data && !error && <ModeNote mode={data.mode} simulated={data.scope?.simulated || view.simulated}/>}
		{inProgress && <InProgressNote/>}
		{!!data && !empty && data.tier?.none && <NoTierNote/>}

		{empty && !error && !inProgress && <NoPassHint data={data} onShowLastPass={() => change({ window: 'last_pass', days: null })}/>}
		{!(empty && !error) && <>
				{noPushes(data)
					? <NoPushesNote data={data}/>
					: <Headline data={data} view={view} loading={loading || (!data && !error)} askable={askable}/>}
				<Surface padded={false} sx={{ ...setupScopeSx, mb: 3 }}>
					<Box sx={{ p: 2.5, position: 'relative' }}>
						<SurfaceHeader icon="table-cells" title="Market and engine, pick-up by pick-up"
							subtitle="Net change over the window, per ACRISS and pick-up date · engine cells add Δ = engine change − market change"
							info={<Methodology/>}
							actions={askable && cells.length ? <AskIrisButton variant="label" tooltip="Ask Iris about this map" getAsk={() => gridEvidence({ data, view })}/> : null}/>
						{loading || !data
							? <Box sx={{ height: 240, borderRadius: '10px', backgroundColor: dsColorNew.tableRowAlt }} aria-busy="true"/>
							: <Box sx={{ opacity: refetching ? 0.55 : 1, transition: 'opacity .2s' }}>
								<ChangeGrid data={data} sel={view.sel} todayIso={todayIso} onSelectPickup={selectPickup} onOpenCell={openCell}/>
								<ChangeLegend unit={view.unit} lor={data.sample?.lor || view.lor} dates={(data.sample?.dates || []).length}/>
							</Box>}
						{refetching && <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
							<Box component="span" sx={{ backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '999px', px: 1.75, py: 0.75, fontSize: 13, color: dsColorNew.textMuted, boxShadow: '0 12px 32px rgba(12,26,62,.12)' }}>Updating…</Box>
						</Box>}
					</Box>
				</Surface>

				{!!view.sel?.pickup && !!data && <Surface padded={false} sx={{ ...setupScopeSx, mb: 3 }}>
					<Box sx={{ p: 2.5 }}>
						<SurfaceHeader icon="paper-plane" title="Pushes of the selected cell"
							subtitle="Every pass that decided it in the window, newest first, including the ones that did not change the price. Each row opens Activity log filtered by its pass and cell."/>
						<PushesTable sel={view.sel} pushes={pushes} view={view} data={data} todayIso={todayIso} onClear={() => change({ sel: null })} onOpenCell={openCell}/>
					</Box>
				</Surface>}

				{!!data && <Surface padded={false} sx={{ ...setupScopeSx, mb: 3 }}>
					<Box sx={{ p: 2.5 }}>
						<SurfaceHeader icon="chart-simple" title="Movement by ACRISS" subtitle={`Average change over all ${(data.sample?.dates || []).length} pick-up dates · you vs your tier · the same formula as the cards`}
							actions={askable && (data.by_acriss || []).length ? <AskIrisButton variant="label" tooltip="Ask Iris which ACRISS moved differently from the tier" getAsk={() => tableEvidence({ data, view, metric })}/> : null}/>
						<MovementCard data={data} view={view} metric={metric} onMetric={setMetric} onDetail={sipp => setAcrissOpen(sipp)}/>
						<MovementTable data={data} view={view} onDetail={sipp => setAcrissOpen(sipp)}/>
					</Box>
				</Surface>}
			</>}

		<CellDetailDialog open={cellOpen && !!cell} cell={cell} data={data} view={view} todayIso={todayIso} onClose={closeCell}
			onOpenAcriss={sipp => { setCellOpen(false); setAcrissOpen(sipp) }} askable={askable}/>
		<AcrissDetailDialog open={!!acrissOpen} sipp={acrissOpen} data={data} view={view} todayIso={todayIso} onClose={() => setAcrissOpen(null)}
			onOpenCell={(sipp, d) => { setAcrissOpen(null); openCell(sipp, d) }} askable={askable}/>
		{!!data && !empty && <Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mb: 2 }}>
			Nothing here approves, discards or sends a price: that stays in Activity log and Executions.
		</Typography>}
	</PageWrapper>
}
